import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type {
  Access_Type_Enum,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { hasPermission } from './hasPermission';
import { type HasPermission } from './Permission';
import { useEntityPermissions } from './useEntityPermissions';
import { useRoleAccess } from './useRoleAccess';
import { useTypePermissions } from './useTypePermissions';

export const useHasPermissionQuery: HasPermission = (
  permission,
  parentObject,
  canHaveAccessAsContributor
) => {
  const permissionArray = Array.isArray(permission) ? permission : [permission];
  const parsed = permissionArray.map((p) => p.split(':'));

  if (parsed.some((p) => p.length !== 2)) {
    throw new Error('Invalid permission');
  }

  const { user } = useRisksmartUser();
  const roleAccess = useRoleAccess();
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const { hasTypePermission, isLoading: typeLoading } = useTypePermissions();
  const entityPerms = useEntityPermissions(parentObject?.Id);

  const nonTRPCPermitted = parsed.some(([accessType, objectType]) => {
    return hasPermission({
      parentObject,
      userId: user?.userId,
      roleAccess,
      objectType: objectType as Parent_Type_Enum,
      accessType: accessType as Access_Type_Enum,
      canHaveAccessAsContributor,
    });
  });

  if (!roleAccess) {
    return {
      hasPermission: false,
      loading: false,
    };
  }

  // Route through the batch hooks when trpc is enabled. This reduces 50–70
  // individual bulkCheck queries to 1 type-level + 1 entity-level query per
  // detail page.
  if (trpcEnabled) {
    if (!parentObject) {
      const allowed = parsed.some(
        ([accessType, objectType]) =>
          !!accessType &&
          !!objectType &&
          hasTypePermission(accessType, objectType)
      );

      return { hasPermission: allowed, loading: typeLoading };
    }

    // Instance-level check
    const allowed = parsed.some(([accessType]) => {
      switch (accessType) {
        case 'read':
          return entityPerms.canRead;
        case 'update':
          return entityPerms.canUpdate;
        case 'delete':
          return entityPerms.canDelete;
        case 'insert':
        case 'create':
          return entityPerms.canInsertChild;
        default:
          return false;
      }
    });

    return { hasPermission: allowed, loading: entityPerms.isLoading };
  }

  return {
    hasPermission: nonTRPCPermitted,
    loading: false,
  };
};
